import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from './axiosInstance';
import { normalizeAxiosError } from '@/lib/error';
import type { GenericErrorResponse } from '@/types/api.types';
import type { RushPrioritySettingResponse, RushPrioritySettingUpdateRequest } from '@/types/userSettings.types';

type ApiResult<T> = { data?: T; error?: GenericErrorResponse };

export const RUSH_PRIORITY_SETTING_QUERY_KEY = ['user-settings', 'rush-priority'];

// List queries whose order depends on the rush priority setting; prefixes of their query keys.
export const RUSH_PRIORITY_LIST_QUERY_KEYS = [['services'], ['hi-work-orders'], ['ddi-work-orders']];

export const getRushPrioritySetting = async (): Promise<ApiResult<RushPrioritySettingResponse>> => {
  try {
    const response = await axiosInstance.get<RushPrioritySettingResponse>('/user-settings/rush-priority');
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const updateRushPrioritySetting = async (
  body: RushPrioritySettingUpdateRequest
): Promise<ApiResult<RushPrioritySettingResponse>> => {
  try {
    const response = await axiosInstance.put<RushPrioritySettingResponse>('/user-settings/rush-priority', body);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

const unwrap = async <T>(request: Promise<ApiResult<T>>): Promise<T> => {
  const { data, error } = await request;
  if (error) throw error;
  return data!;
};

export const useRushPrioritySetting = () =>
  useQuery<RushPrioritySettingResponse, GenericErrorResponse>({
    queryKey: RUSH_PRIORITY_SETTING_QUERY_KEY,
    queryFn: () => unwrap(getRushPrioritySetting()),
  });

export const useUpdateRushPrioritySetting = () => {
  const queryClient = useQueryClient();
  return useMutation<RushPrioritySettingResponse, GenericErrorResponse, RushPrioritySettingUpdateRequest>({
    mutationFn: (body) => unwrap(updateRushPrioritySetting(body)),
    onSuccess: (saved) => {
      queryClient.setQueryData(RUSH_PRIORITY_SETTING_QUERY_KEY, saved);
      return Promise.all(
        RUSH_PRIORITY_LIST_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
      );
    },
  });
};
